import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import ConfirmModal from "../../../../../../components/ConfirmModal";
import * as Fonts from "../../../../../../components/Fonts";
import Input from "../../../../../../components/Form/Input/input";
import { Select } from "../../../../../../components/Form/Input/select";
import Label from "../../../../../../components/Form/Label";
import { FormGroup } from "../../../../../../components/Form/styles";
import SaveBar from "../../../../../../components/SaveBar";
import { SubHeader } from "../../../../../../components/SubHeader";
import ToggleSwitch from "../../../../../../components/ToggleSwitch";
import { useCurrentPage, withPage } from "../../../../../../hooks/CurrentPage";
import {
  DATE_FIELD_FORMATS,
  DateFieldFormat,
  FORM_FIELD_TYPES,
  FormCustomField,
  FormFieldType,
  newFieldOption,
  newFormField,
  SELECT_FIELD_DISPLAYS,
} from "../../../../../../model/formFields";
import { clone, deepEqual } from "../../../../../../utils/objects";
import { classNames } from "../../../../../../utils/styles";
import { navigationState } from "../../../Sections/navigationState";
import { FieldTypeIcon } from "../../components/FieldTypeIcon";
import { FormNavigationState } from "../../formNavigationState";
import { Card, Section } from "../styles";
import { OptionsList } from "./components/OptionsList";
import { Divided, Hint, Page, ToggleRow, TypeGrid, Warning } from "./styles";

// How a selection field is shown: a dropdown, radio buttons or checkboxes
const SelectDisplayIcon = ({ display }: { display: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {display === "DROPDOWN" && (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m9 11 3 3 3-3" />
      </>
    )}
    {display === "RADIO" && (
      <>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3.5" fill="currentColor" />
      </>
    )}
    {display === "CHECKBOX" && (
      <>
        <rect x="3" y="3" width="18" height="18" rx="4" />
        <path d="m8 12 3 3 5-6" />
      </>
    )}
  </svg>
);

// The type each old fixed field was drawn as; changing it unlinks the field from the old renderer
const LEGACY_TYPES: Record<string, FormFieldType> = {
  name: "SHORT_TEXT",
  email: "EMAIL",
  phone: "PHONE",
  message: "LONG_TEXT",
};

/** Creates or edits one field of the form section; hands the result back to /editor/form. */
const FormFieldPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formNavigationState = navigationState.get() as FormNavigationState | undefined;
  const fields = formNavigationState?.section.customFields || [];
  const isNew = formNavigationState?.editingFieldId === "new";
  const original = isNew ? undefined : fields.find(({ id }) => id === formNavigationState?.editingFieldId);

  const [field, setField] = useState<FormCustomField>(() => (original ? clone(original) : newFormField()));
  const [isConfirmingRemove, setConfirmingRemove] = useState(false);
  const { pageId } = useCurrentPage();

  // Opened without coming from the form screen (e.g. a reload): nothing to edit, back to the page's sections
  if (!formNavigationState || (!isNew && !original)) {
    return <Navigate to={withPage("/editor/sections", pageId)} replace />;
  }

  const prefix = "pages.editor.form.field";
  const isCheckbox = field.type === "CHECKBOX";
  const isSelect = field.type === "SELECT";
  const options = field.options || [];
  // Hidden options don't count: the visitor must still have at least two to choose from
  const visibleFilledOptions = options.filter(option => option.active !== false && option.label.trim());
  const canSave = !!field.label.trim() && (!isSelect || visibleFilledOptions.length >= 2);
  const hasUnsavedChanges = isNew ? !!field.label || field.type !== "SHORT_TEXT" : !deepEqual(field, original);

  // Suggests a name for the chosen type, unless the user already wrote their own
  const suggestionFor = (type: FormFieldType) => t(`pages.editor.form.fieldSuggestions.${type}`);
  const isSuggestedLabel = !field.label.trim() || field.label === suggestionFor(field.type);

  const setType = (type: FormFieldType) =>
    setField({
      ...field,
      type,
      label: isSuggestedLabel ? suggestionFor(type) : field.label,
      options: type === "SELECT" ? (options.length ? options : [newFieldOption(), newFieldOption()]) : undefined,
    });

  const backToForm = (customFields: FormCustomField[]) => {
    navigationState.set({
      ...formNavigationState,
      section: { ...formNavigationState.section, customFields },
      editingFieldId: undefined,
    });
    navigate(-1);
  };

  const save = () => {
    if (!canSave) {
      return;
    }
    const saved: FormCustomField = {
      ...field,
      label: field.label.trim(),
      options: isSelect
        ? options.filter(option => option.label.trim()).map(option => ({ ...option, label: option.label.trim() }))
        : undefined,
      // Only the settings of the chosen type are kept
      selectDisplay: isSelect ? field.selectDisplay || "DROPDOWN" : undefined,
      dateFormat: field.type === "DATE" ? field.dateFormat || "DATE" : undefined,
      phoneFormat: field.type === "PHONE" ? field.phoneFormat || "BR" : undefined,
      legacyKey: field.legacyKey && LEGACY_TYPES[field.legacyKey] === field.type ? field.legacyKey : undefined,
    };
    backToForm(isNew ? [...fields, saved] : fields.map(item => (item.id === saved.id ? saved : item)));
  };

  return (
    <Page>
      <SubHeader
        title={isNew ? t(`${prefix}.newTitle`) : t(`${prefix}.editTitle`)}
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.form.header.title"), to: "/editor/form" },
        ]}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      <Section>
        <Fonts.Label>{t(`${prefix}.type`)}</Fonts.Label>
        <TypeGrid role="radiogroup" aria-label={t(`${prefix}.type`)}>
          {FORM_FIELD_TYPES.map(type => (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={field.type === type}
              className={classNames({ selected: field.type === type })}
              onClick={() => setType(type)}>
              <FieldTypeIcon type={type} size={22} />
              {t(`pages.editor.form.fieldTypes.${type}`)}
            </button>
          ))}
        </TypeGrid>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.section`)}</Fonts.Label>
        <Card
          as="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            save();
          }}>
          <FormGroup>
            <Input
              id="label"
              type="text"
              maxLength={isCheckbox ? 200 : 60}
              value={field.label}
              label={isCheckbox ? t(`${prefix}.labelCheckbox`) : t(`${prefix}.label`)}
              onChange={e => setField({ ...field, label: e.target.value })}
            />
          </FormGroup>
          {/* A date field shows a picker, not a text box with the name inside */}
          {field.type !== "DATE" && (
            <Hint>{isCheckbox ? t(`${prefix}.labelCheckboxHint`) : t(`${prefix}.labelHint`)}</Hint>
          )}

          {field.type === "DATE" && (
            <Divided>
              <FormGroup>
                <Select
                  id="dateFormat"
                  value={field.dateFormat || "DATE"}
                  onChange={e => setField({ ...field, dateFormat: e.target.value as DateFieldFormat })}>
                  {DATE_FIELD_FORMATS.map(format => (
                    <option key={format} value={format}>
                      {t(`${prefix}.dateFormat.options.${format}`)}
                    </option>
                  ))}
                </Select>
                <Label htmlFor="dateFormat" text={t(`${prefix}.dateFormat.title`)} />
              </FormGroup>
            </Divided>
          )}

          {field.type === "PHONE" && (
            <ToggleRow>
              <div className="text">
                <h6>{t(`${prefix}.phone.international`)}</h6>
                <p className="description">{t(`${prefix}.phone.internationalDescription`)}</p>
              </div>
              <ToggleSwitch
                label={t(`${prefix}.phone.international`)}
                value={field.phoneFormat === "INTERNATIONAL"}
                setValue={international => setField({ ...field, phoneFormat: international ? "INTERNATIONAL" : "BR" })}
              />
            </ToggleRow>
          )}

          <ToggleRow>
            <div className="text">
              <h6>{t(`${prefix}.required`)}</h6>
              <p className="description">{t(`${prefix}.requiredDescription`)}</p>
            </div>
            <ToggleSwitch
              label={t(`${prefix}.required`)}
              value={field.required}
              setValue={required => setField({ ...field, required })}
            />
          </ToggleRow>
        </Card>
      </Section>

      {isSelect && (
        <Section>
          <Fonts.Label>{t(`${prefix}.options.title`)}</Fonts.Label>
          <Card>
            <Fonts.Label>{t(`${prefix}.selectDisplay.title`)}</Fonts.Label>
            <TypeGrid $compact role="radiogroup" aria-label={t(`${prefix}.selectDisplay.title`)}>
              {SELECT_FIELD_DISPLAYS.map(display => (
                <button
                  key={display}
                  type="button"
                  role="radio"
                  aria-checked={(field.selectDisplay || "DROPDOWN") === display}
                  className={classNames({ selected: (field.selectDisplay || "DROPDOWN") === display })}
                  onClick={() => setField({ ...field, selectDisplay: display })}>
                  <SelectDisplayIcon display={display} />
                  {t(`${prefix}.selectDisplay.options.${display}`)}
                </button>
              ))}
            </TypeGrid>
            <Hint className="below-grid">
              {t(`${prefix}.selectDisplay.descriptions.${field.selectDisplay || "DROPDOWN"}`)}
            </Hint>

            <Divided>
              <OptionsList options={options} onChange={options => setField({ ...field, options })} />
              {visibleFilledOptions.length < 2 && <Warning>{t(`${prefix}.options.warning`)}</Warning>}
            </Divided>
          </Card>
        </Section>
      )}

      <SaveBar
        sticky
        onSave={save}
        disabled={!canSave}
        showTrashAction={!isNew}
        onTrashClick={() => setConfirmingRemove(true)}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {isConfirmingRemove && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.editor.form.fields.remove.confirmModal.title")}
          cancelTitle={t("pages.editor.form.fields.remove.confirmModal.cancel")}
          confirmButtonTitle={t("pages.editor.form.fields.remove.confirmModal.confirm")}
          onCancel={() => setConfirmingRemove(false)}
          onConfirm={() => backToForm(fields.filter(({ id }) => id !== field.id))}
        />
      )}
    </Page>
  );
};

export default FormFieldPage;
